import {
  Box,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from "@stripe/react-stripe-js";
import { Controller, useFormContext } from "react-hook-form";
import AutomatedMessageInfo from "@/components/booking/automatedMessageInfo";
import MarketingConsent from "@/components/booking/marketingConsent";
import { CollectCreditCardFormInput } from "@/components/booking/steps/collectCreditCard";
import LoadingButton from "@/components/common/buttons/loadingButton";
import { ConfirmationCheckboxes } from "@/components/common/checkbox/confirmationCheckboxes";
import { ERROR_PRIMARY } from "@/config/mui/colorPalette";
import { useClientBookingContext } from "@/contexts/clientBookingContext";
import useAction from "@/hooks/misc/useAction";
import { PaymentCardValidationInfo } from "@/hooks/payments/useCreditCardInputs";
import AgreementCheckbox from "../agreementCheckbox";
import { CancellationPolicy } from "../cancellationPolicy";
import ClientAffirmationLabel from "../clientAffirmationLabel";
import DepositAdditionalInfo from "../depositAdditionalInfo";
import { StripeField } from "./stripeField";

type Props = {
  paymentCardValidationInfo: PaymentCardValidationInfo;
  showPaymentCardEmptyFieldErrors: boolean;
  onElementChange: (
    field: string,
    errorField: string
  ) => (changeEvent: { complete: boolean; error: { message: string } }) => void;
  setupCard: () => Promise<void>;
  showConsentToCharge?: boolean;
  showAutomatedMessage?: boolean;
  title?: string;
  buttonTitle?: string;
};

export default function PaymentCardFormSection({
  paymentCardValidationInfo,
  showPaymentCardEmptyFieldErrors,
  onElementChange,
  setupCard,
  showConsentToCharge = true,
  showAutomatedMessage = true,
  title = "Secure your appointment by card",
  buttonTitle = "Book appointment",
}: Props) {
  const {
    cardNumberError,
    expiredError,
    cvcError,
    cardNumberComplete,
    expiredComplete,
    cvcComplete,
  } = paymentCardValidationInfo;

  const {
    control,
    watch,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<CollectCreditCardFormInput>();

  const {
    medspaData,
    isReturningClient,
    depositsEnabled,
    depositPaymentError,
    onlineBookingDepositsV1Enabled,
    setDepositPaymentError,
  } = useClientBookingContext();

  const {
    cancellationFeePolicyEnabled,
    lateCancellationAdvanceHours,
    noShowFeeValue,
    allowDeposits,
    depositAmount,
    clientAffirmation,
  } = medspaData?.configuration || {};

  const shouldShowAgeConsent = !isReturningClient;

  const { handleAction, isLoading } = useAction(setupCard);

  const onElementFocus = () => {
    if (depositPaymentError) setDepositPaymentError("");
  };

  const checkboxesToWatch: (keyof CollectCreditCardFormInput)[] = [
    "marketingConsent",
    "ageConsent",
  ];
  if (showConsentToCharge) {
    checkboxesToWatch.unshift("consentToCharge");
  }

  return (
    <Box>
      <Typography
        variant="h3"
        sx={{
          color: "text.primary",
          mb: 3,
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="paragraphSmall"
        sx={{
          color: "secondary.main",
          mb: 3,
        }}
      >
        A credit or debit card is required to book your appointment.
      </Typography>
      {showConsentToCharge && depositsEnabled && (
        <DepositAdditionalInfo depositAmount={depositAmount} sx={{ my: 3 }} />
      )}
      <Typography
        variant="subtitleSmall"
        sx={{
          color: "secondary.main",
          mb: 1,
        }}
      >
        Card information
      </Typography>
      <Grid container spacing={1}>
        <Grid size={12}>
          <StripeField
            stripeElement={CardNumberElement}
            error={
              Boolean(cardNumberError) ||
              (!cardNumberComplete && showPaymentCardEmptyFieldErrors) ||
              Boolean(depositPaymentError)
            }
            labelErrorMessage={cardNumberError}
            onChange={onElementChange("cardNumberComplete", "cardNumberError")}
            onFocus={onElementFocus}
          />
        </Grid>

        <Grid size={6}>
          <StripeField
            stripeElement={CardExpiryElement}
            error={
              Boolean(expiredError) ||
              (!expiredComplete && showPaymentCardEmptyFieldErrors) ||
              Boolean(depositPaymentError)
            }
            labelErrorMessage={expiredError}
            onChange={onElementChange("expiredComplete", "expiredError")}
            onFocus={onElementFocus}
          />
        </Grid>

        <Grid size={6}>
          <StripeField
            stripeElement={CardCvcElement}
            error={
              Boolean(cvcError) ||
              (!cvcComplete && showPaymentCardEmptyFieldErrors) ||
              Boolean(depositPaymentError)
            }
            labelErrorMessage={cvcError}
            onChange={onElementChange("cvcComplete", "cvcError")}
            onFocus={onElementFocus}
          />
        </Grid>
        {depositPaymentError && (
          <Typography
            variant="labelSmall"
            color={ERROR_PRIMARY}
            sx={{ mt: 2, pl: 1 }}
          >
            Double check your credit card info or try another card.
          </Typography>
        )}
        <Grid size={12} sx={{ mt: { xs: 3, "ipad-ver": 2 } }}>
          <Controller
            name="zipCode"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                fullWidth
                type="text"
                placeholder="Billing zip code"
                error={!!errors.zipCode}
                helperText={
                  errors.zipCode ? (errors.zipCode.message as string) : ""
                }
              />
            )}
          />
        </Grid>
        <ConfirmationCheckboxes
          getValues={getValues}
          setValue={setValue}
          checkboxNames={checkboxesToWatch}
        >
          {showConsentToCharge && (
            <Grid
              size={12}
              sx={{
                mt: 1,
              }}
            >
              <Controller
                name="consentToCharge"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <>
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value}
                          sx={{
                            color: errors.consentToCharge ? "error.main" : "",
                            alignSelf: "flex-start",
                          }}
                        />
                      }
                      label={
                        onlineBookingDepositsV1Enabled ? (
                          <ClientAffirmationLabel
                            cancellationFeePolicyEnabled={
                              cancellationFeePolicyEnabled
                            }
                            customClientAffirmation={clientAffirmation}
                            lateCancellationHours={lateCancellationAdvanceHours}
                            noShowFeeValue={noShowFeeValue}
                            depositAmount={depositAmount}
                            allowDeposits={allowDeposits}
                          />
                        ) : (
                          <CancellationPolicy
                            lateCancellationHours={lateCancellationAdvanceHours}
                            noShowFeeValue={noShowFeeValue}
                          />
                        )
                      }
                    />
                    <FormHelperText error={!!errors.consentToCharge}>
                      {(errors.consentToCharge?.message as string) ?? ""}
                    </FormHelperText>
                  </>
                )}
              />
            </Grid>
          )}

          <MarketingConsent
            name="marketingConsent"
            control={control}
            errors={errors}
          />

          {shouldShowAgeConsent && (
            <Grid
              size={12}
              sx={{
                mt: 1,
              }}
            >
              <AgreementCheckbox
                control={control}
                medspaId={medspaData.id}
                name="ageConsent"
                errors={errors}
              />
            </Grid>
          )}
        </ConfirmationCheckboxes>
      </Grid>
      <LoadingButton
        fullWidth
        sx={{ mt: 3 }}
        variant="contained"
        onClick={handleSubmit(handleAction)}
        loading={isLoading}
        disabled={
          (showConsentToCharge && !watch("consentToCharge")) ||
          (shouldShowAgeConsent && !watch("ageConsent")) ||
          !watch("marketingConsent")
        }
      >
        {buttonTitle}
      </LoadingButton>
      {showAutomatedMessage && <AutomatedMessageInfo />}
    </Box>
  );
}
